import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TextareaModule } from 'primeng/textarea';
import { ChipModule } from 'primeng/chip';
import { DividerModule } from 'primeng/divider';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastService } from '@workspace-app/shared';
import { Bill, BillDataService, BillEmailTemplate, BillRecipientsInfo } from '../bill-data.service';

@Component({
  selector: 'app-send-email-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
    ChipModule,
    DividerModule,
    ProgressSpinnerModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-column gap-3">
      @if (loading()) {
        <div class="flex justify-content-center p-5">
          <p-progressspinner />
        </div>
      } @else {
        <!-- Template selector -->
        <div class="flex flex-column gap-1">
          <label class="font-medium text-sm">Template</label>
          <p-select
            [options]="templates()"
            [(ngModel)]="selectedTemplate"
            optionLabel="brand"
            placeholder="Select template"
            class="w-full"
            (onChange)="onTemplateChange()" />
        </div>

        <!-- Subject -->
        <div class="flex flex-column gap-1">
          <label class="font-medium text-sm">Subject</label>
          <input pInputText type="text" [(ngModel)]="subject" class="w-full" />
        </div>

        <!-- Recipients -->
        <div class="flex flex-column gap-1">
          <label class="font-medium text-sm">To</label>
          <div class="flex flex-wrap gap-1">
            @for (r of recipients(); track r) {
              <p-chip [label]="r" />
            }
            @if (recipients().length === 0) {
              <span class="text-color-secondary text-sm">No recipients found</span>
            }
          </div>
        </div>

        <!-- CC -->
        <div class="flex flex-column gap-1">
          <label class="font-medium text-sm">CC (comma-separated)</label>
          <input pInputText type="text" [(ngModel)]="ccField" class="w-full" placeholder="email@example.com" />
        </div>

        <p-divider />

        <!-- Intro text -->
        <div class="flex flex-column gap-1">
          <label class="font-medium text-sm">Introduction</label>
          <textarea pTextarea [(ngModel)]="introText" rows="4" class="w-full" autoResize="true"></textarea>
        </div>

        <!-- Invoice details (read-only info) -->
        <div class="surface-ground p-3 border-round text-sm">
          <div><strong>Invoice Number:</strong> {{ bill.completeBillNumber }}</div>
          <div><strong>Billing Year:</strong> {{ bill.billingYear }}</div>
          <div><strong>Total Amount:</strong> {{ bill.currency }} {{ bill.totalAmount | number: '1.2-2' }}</div>
        </div>

        <!-- Closing text -->
        <div class="flex flex-column gap-1">
          <label class="font-medium text-sm">Closing</label>
          <textarea pTextarea [(ngModel)]="closingText" rows="4" class="w-full" autoResize="true"></textarea>
        </div>

        <p-divider />

        <!-- Actions -->
        <div class="flex justify-content-between align-items-center">
          <p-button
            label="Preview"
            icon="pi pi-eye"
            severity="secondary"
            [outlined]="true"
            [loading]="previewing()"
            (onClick)="previewEmail()" />
          <div class="flex gap-2">
            <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="onClose()" />
            <p-button
              label="Send"
              icon="pi pi-send"
              [loading]="sending()"
              [disabled]="recipients().length === 0 || !subject"
              (onClick)="sendEmail()" />
          </div>
        </div>
      }
    </div>
  `,
})
export class SendEmailDialogComponent implements OnInit {
  private ref = inject(DynamicDialogRef);
  private config = inject(DynamicDialogConfig);
  private billDataService = inject(BillDataService);
  private toastService = inject(ToastService);

  bill: Bill = this.config.data.bill;

  templates = signal<BillEmailTemplate[]>([]);
  recipients = signal<string[]>([]);
  loading = signal(true);
  previewing = signal(false);
  sending = signal(false);

  selectedTemplate: BillEmailTemplate | null = null;
  subject = '';
  introText = '';
  closingText = '';
  ccField = '';

  ngOnInit(): void {
    // Load templates and recipients in parallel
    this.billDataService.getEmailTemplates().subscribe({
      next: (templates) => {
        this.templates.set(templates);
        // Auto-select default template
        const defaultTemplate = templates.find((t) => t.isDefault) ?? templates[0];
        if (defaultTemplate) {
          this.selectedTemplate = defaultTemplate;
          this.applyTemplate(defaultTemplate);
        }
        this.loading.update((v) => (this.recipients() !== undefined ? false : v));
      },
      error: () => this.loading.set(false),
    });

    this.billDataService.getBillRecipients(this.bill.id).subscribe({
      next: (info: BillRecipientsInfo) => {
        const emails: string[] = [];
        if (info.addressEmail) {
          emails.push(info.addressEmail);
        }
        info.contacts.forEach((c) => {
          if (c.email && !emails.includes(c.email)) {
            emails.push(c.email);
          }
        });
        this.recipients.set(emails);
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  onTemplateChange(): void {
    if (this.selectedTemplate) {
      this.applyTemplate(this.selectedTemplate);
    }
  }

  private applyTemplate(template: BillEmailTemplate): void {
    const orgName = this.bill.recipientOrganisation?.name ?? 'Customer';
    this.subject = template.subjectPattern
      .replace('{BillingYear}', this.bill.billingYear.toString())
      .replace('{CompanyName}', orgName)
      .replace('{BillNumber}', this.bill.completeBillNumber);
    this.introText = this.stripHtml(template.introText);
    this.closingText = this.stripHtml(template.closingText);
  }

  private stripHtml(html: string): string {
    let text = html;
    text = text.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<\/p>\s*<p>/gi, '\n');
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return (doc.body.textContent || '').trim();
  }

  private toHtml(text: string): string {
    return text
      .split('\n')
      .map((line) => `<p>${line || '&nbsp;'}</p>`)
      .join('');
  }

  previewEmail(): void {
    this.previewing.set(true);
    this.billDataService
      .previewBillEmail(this.bill.id, {
        subject: this.subject,
        introText: this.toHtml(this.introText),
        closingText: this.toHtml(this.closingText),
      })
      .subscribe({
        next: (preview) => {
          this.previewing.set(false);
          // Open preview in new window
          const win = window.open('', '_blank', 'width=700,height=600');
          if (win) {
            win.document.write(preview.htmlBody);
            win.document.close();
          }
        },
        error: () => {
          this.previewing.set(false);
          this.toastService.displayErrorMessage('Failed to generate preview');
        },
      });
  }

  sendEmail(): void {
    if (this.recipients().length === 0 || !this.subject) return;

    const cc = this.ccField
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    this.sending.set(true);
    this.billDataService
      .sendBillEmail(this.bill.id, {
        subject: this.subject,
        introText: this.toHtml(this.introText),
        closingText: this.toHtml(this.closingText),
        recipients: this.recipients(),
        cc: cc.length > 0 ? cc : undefined,
        attachPdf: true,
      })
      .subscribe({
        next: (updatedBill) => {
          this.sending.set(false);
          this.toastService.displaySuccessMessage('Email sent successfully');
          this.ref.close({ sent: true, bill: updatedBill });
        },
        error: (err) => {
          this.sending.set(false);
          this.toastService.displayErrorMessage(err.error?.message || 'Failed to send email');
        },
      });
  }

  onClose(): void {
    this.ref.close();
  }
}
